import { useState, useEffect, useReducer, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap'
import s from './CreateProposalFromTemplateModal.scss'
import * as ACT from './actions'
import { If } from 'components/ideal-core-ui'
import { SelectAddProposalTemplate } from './SelectAddProposalTemplate'
import { EditProposalTemplate } from './EditProposalTemplate'
import { SelectProposalTemplateClients } from './SelectProposalTemplateClients'
import { createSelector } from 'reselect'
import update from 'immutability-helper'
import cn from 'classnames'
import { useLoading } from 'hooks'
import { makeGetRequest } from 'utils/api'
import { toCategories } from 'components/Admin/Organization/Templates/ProposalHtmlTemplates/serializers.js'
import { useImmer } from 'use-immer'

const initialPages = [
  {
    content: []
  }
]

const selectProposalTemplate = createSelector(
  [
    state => state.proposals.templates,
    state => state.deal.templates,
    state => state.deal.selectedProposalTemplate
  ],
  (templates, dealTemplates, selectedProposalTemplate) => {
    if (!selectedProposalTemplate) return null

    const selectedGlobalTemplate = templates.filter(
      template => template.id === selectedProposalTemplate.value
    )[0]
    const selectedDealTemplate = dealTemplates.filter(
      template => template.id === selectedProposalTemplate.value
    )[0]
    // this is augumented with content because the propsoals templates list used to contain the content
    // but now it doesn't. because the content is loaded on demand when a selection is made
    if (selectedGlobalTemplate) {
      return { ...selectedGlobalTemplate, content: selectedProposalTemplate.content }
    }
    if (selectedDealTemplate) {
      return selectedDealTemplate
    }
  }
)

const companiesReducer = (state, action) => {
  const { companyId, proposalId } = action.payload
  const index = state.findIndex(company => company.companyId === companyId)

  switch (action.type) {
    case ACT.TOGGLE_CLIENT_ACTION:
      if (~index) {
        return state.filter(company => company.companyId !== companyId)
      } else {
        return [
          ...state,
          {
            companyId,
            proposalId: 0
          }
        ]
      }
    case ACT.CHANGE_PROPOSAL_ACTION:
      return update(state, {
        [index]: {
          proposalId: { $set: proposalId }
        }
      })
    default:
      return state
  }
}

const CreateProposalFromTemplateModalContainer = ({
  showCreateProposalFromTemplateModal,
  toggleCreateProposalFromTemplateModal,
  dealId,
  dealCompanies,
  proposalsByClient,
  dealValue,
  templateType,
  createProposalCb
}) => {
  const initialState = useMemo(() => {
    return proposalsByClient
      ? Object.keys(proposalsByClient).map(p => {
          const proposal = proposalsByClient[p]
          const isArray = Array.isArray(proposal)
          return {
            companyId: isArray ? proposal[0].company.id : +proposal,
            proposalId: isArray ? proposal[proposal.length - 1].id : 0
          }
        })
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalsByClient])

  const [step, setStep] = useState(0)
  const [infoOpened, setInfoOpened] = useState(true)
  const [selectedGlobalTemplate, setSelectedGlobalTemplate] = useState(null)
  const [selectedDealTemplate, setSelectedDealTemplate] = useState(null)
  const [editedProposalTemplate, setEditedProposalTemplate] = useState('')
  const [editedProposalTemplateBase64, setEditedProposalTemplateBase64] = useState('')
  const [baseBid, setBaseBid] = useState('')
  const [clientsProposals, localDispatch] = useReducer(companiesReducer, initialState)

  const dispatch = useDispatch()
  const templates = useSelector(state => state.proposals.templates)
  const dealTemplates = useSelector(state => state.deal.templates)
  const selectedTemplate = useSelector(state => selectProposalTemplate(state))
  const editedSelectedProposalTemplate = useSelector(
    state => state.deal.editedSelectedProposalTemplate
  )
  const [, withLoadingLocks] = useLoading()
  const [categories, setCategories] = useState([])
  const [pages, setPages] = useImmer(initialPages)
  const [htmlContent, setHtmlContent] = useState('')
  const [pageType, setPageType] = useState('letter')

  const generateProposals = () => {
    dispatch({
      type: ACT.DEAL_GENERATE_PROPOSALS_REQUESTED,
      payload: {
        editedProposalTemplateBase64:
          templateType === 'WORD' ? editedProposalTemplateBase64 : htmlContent,
        dealId,
        editedSelectedProposalTemplate,
        clientsProposals,
        baseBid: Number(baseBid).toFixed(2),
        pageType,
        templateType,
        ...(createProposalCb ? { createProposalCb } : {})
      }
    })

    toggleCreateProposalFromTemplateModal()
  }

  const addSelectedTemplateToStore = value => {
    dispatch({
      type: ACT.DEAL_PROPOSALS_ADD_SELECTED_TEMPLATE_TO_STORE,
      payload: {
        selectedProposalTemplate: value
      }
    })
  }

  useEffect(() => {
    if (step === 0)
      dispatch({ type: ACT.PROPOSALS_TEMPLATES_FETCH_REQUESTED, payload: { templateType } })
  }, [dispatch, step, templateType])

  useEffect(() => {
    if (step === 0) {
      dispatch({
        type: ACT.DEAL_PROPOSALS_TEMPLATES_FETCH_REQUESTED,
        payload: {
          dealId,
          templateType
        }
      })
    }
  }, [dispatch, dealId, step, templateType])

  useEffect(() => {
    if (dealValue) {
      setBaseBid(dealValue)
    }
  }, [dealValue])

  useEffect(() => {
    ;(async () => {
      if (templateType !== 'HTML') return
      const result = await withLoadingLocks(makeGetRequest('/v1/proposaltemplates/html/sections'))
      const [sections] = toCategories(result)
      setCategories(sections)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (editedProposalTemplate) {
      setPages(JSON.parse(editedProposalTemplate))
    } else {
      if (selectedTemplate) {
        setPages(JSON.parse(selectedTemplate.content))
        setPageType(selectedTemplate.pageType?.toLowerCase())
      }
    }
  }, [editedProposalTemplate, selectedTemplate, setPages])

  const saveProposalDraft = (body, base64body) => {
    setEditedProposalTemplate(templateType === 'HTML' ? JSON.stringify(body) : body)
    templateType !== 'HTML' && setEditedProposalTemplateBase64(base64body)

    dispatch({
      type: ACT.DEAL_SAVE_PROPOSAL_DRAFT,
      payload: {
        selectedGlobalTemplate,
        editedProposalTemplate: templateType === 'HTML' ? JSON.stringify(body) : body,
        dealId,
        selectedTemplate,
        templateType,
        pageType
      }
    })
  }

  return (
    <Modal
      keyboard={false}
      backdrop="static"
      isOpen={showCreateProposalFromTemplateModal}
      toggle={toggleCreateProposalFromTemplateModal}
      size="xl"
      className={s.modal}
      data-cy="add_proposal_from_template_modal"
    >
      <ModalHeader toggle={toggleCreateProposalFromTemplateModal} className={s.modalHeader}>
        {`Create Proposal from ${templateType.toUpperCase()} Template`}
      </ModalHeader>
      <ModalBody className={s.modalBody}>
        <div
          className={cn(s.content, {
            [s.wordEditorContent]: step === 1
          })}
        >
          <If condition={step === 0}>
            <SelectAddProposalTemplate
              selectedGlobalTemplate={selectedGlobalTemplate}
              setSelectedGlobalTemplate={value => {
                if (selectedDealTemplate) setSelectedDealTemplate(null)
                setSelectedGlobalTemplate(value)
                addSelectedTemplateToStore(value)
              }}
              selectedDealTemplate={selectedDealTemplate}
              setSelectedDealTemplate={value => {
                if (selectedGlobalTemplate) setSelectedGlobalTemplate(null)
                setSelectedDealTemplate(value)
                addSelectedTemplateToStore(value)
              }}
              templates={templates}
              dealTemplates={dealTemplates}
              setEditedProposalTemplate={setEditedProposalTemplate}
            />
          </If>
          <If condition={step === 1}>
            <EditProposalTemplate
              templateType={templateType}
              categories={categories}
              pageType={pageType}
              setPageType={setPageType}
              editedProposalTemplate={editedProposalTemplate}
              proposalContent={selectedTemplate ? selectedTemplate.content : ''}
              drawerCallback={setInfoOpened}
              saveActions={[
                {
                  text: 'Save Draft',
                  cb: (body, base64body) => {
                    saveProposalDraft(body, base64body)
                  }
                },
                {
                  text: templateType === 'WORD' ? 'Save & Go to Next Step (Generate)' : 'Next Step',
                  className: s.greenBtn,
                  cb: (body, base64body) => {
                    saveProposalDraft(body, base64body)
                    setStep(2)
                    templateType === 'HTML' && setHtmlContent(base64body)
                  },
                  enabled: true,
                  cy: 'save_next_step_btn'
                }
              ]}
              pages={pages}
            />
          </If>
          <If condition={step === 2}>
            <SelectProposalTemplateClients
              baseBid={baseBid}
              setBaseBid={setBaseBid}
              clientsProposals={clientsProposals}
              dispatch={localDispatch}
              proposalsByClient={proposalsByClient}
              dealId={dealId}
              companies={dealCompanies}
            />
          </If>
        </div>

        <div
          className={cn(s.actionBtns, {
            [s.positionTop]: step === 1,
            [s.opened]: infoOpened,
            [s.html]: templateType === 'HTML'
          })}
        >
          <If condition={step === 0}>
            <Button
              disabled={!selectedGlobalTemplate && !selectedDealTemplate}
              className={s.saveBtn}
              onClick={() => setStep(step + 1)}
              data-cy="next_step"
            >
              Go to Next Step (Edit Proposal)
            </Button>
          </If>
          <If condition={step === 2}>
            <Button
              className={s.saveBtn}
              onClick={generateProposals}
              disabled={!clientsProposals.length || !baseBid}
              data-cy="generate_proposal_btn"
            >
              Generate Proposals
            </Button>
          </If>
          <If condition={step !== 0}>
            <Button className={s.backBtn} color="secondary" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          </If>
          <Button color="secondary" onClick={toggleCreateProposalFromTemplateModal}>
            Cancel
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default CreateProposalFromTemplateModalContainer
